/**
 * **Copy from screen…** — columns or conditions taken from another screen into
 * the one being edited (`docs/plans/group-sharing.md`, step 5: building the
 * Combo screen out of JAS's and JP's).
 *
 * The island lists and copies; it never fetches. The host injects the screens
 * the author can read and a loader for one of them — the `resolveToken` /
 * `compileExpr` seam — so this package stays WebView-hostable and knows no URL.
 *
 * Pure. Additive, as a column set is (`column-sets.ts`), and for that file's
 * reason: nothing on the screen is removed, reordered or overwritten, so a copy
 * cannot spoil what the author already built, and undoing one is deleting what
 * they did not want.
 */

import type { SavedScreenerFilter, ScreenerColumn } from "@qufolio/model";

import { isSealed } from "./saved-filters.js";
import { columnStub, conditionStub, isLinkedColumn, isLinkedCondition } from "./screen-links.js";

/** One screen the author may copy from, as the picker lists it. */
export interface CopySourceScreen {
  key: string;
  name: string;
  /** Who owns it, as a reader names them — `JAS`. The picker groups on this. */
  ownerName: string;
}

/** What the loader answers for one screen: the halves that can be copied. */
export interface CopySourceDefinition {
  columns?: readonly ScreenerColumn[];
  savedFilters?: readonly SavedScreenerFilter[] | null;
}

/**
 * The host's half. Supplied **only while authoring** — copying between screens
 * is how a group's own screen is assembled, and a member's screen has its own
 * Duplicate — so a host that omits it gets no control at all.
 */
export interface CopyFromScreen {
  /** Every screen the author can read; the one being edited is dropped here. */
  screens: readonly CopySourceScreen[];
  /** One screen's definition, or `null` when it cannot be read. */
  load: (key: string) => Promise<CopySourceDefinition | null>;
}

/** The screens grouped by owner, in first-seen order, the current one left out. */
export function copySourcesByOwner(
  screens: readonly CopySourceScreen[],
  current: string | undefined,
): { owner: string; screens: CopySourceScreen[] }[] {
  const groups = new Map<string, CopySourceScreen[]>();
  for (const screen of screens) {
    if (screen.key === current) continue;
    const list = groups.get(screen.ownerName) ?? [];
    list.push(screen);
    groups.set(screen.ownerName, list);
  }
  return [...groups].map(([owner, list]) => ({ owner, screens: list }));
}

/**
 * The screen's columns with the picked ones appended **as they are** — header,
 * width, decimals, notation, format rules, `colorBy`, `computed` — and the
 * fields skipped because this screen already has a column of that field.
 *
 * Skipped rather than overwritten: the column on this screen is the author's,
 * and a second entry of one field would be two columns the grid treats as one.
 */
export function copyColumns(
  current: readonly ScreenerColumn[],
  picked: readonly ScreenerColumn[],
): { columns: ScreenerColumn[]; skipped: string[] } {
  const have = new Set(current.map((c) => c.field));
  const skipped: string[] = [];
  const added: ScreenerColumn[] = [];
  for (const column of picked) {
    if (have.has(column.field)) {
      skipped.push(column.field);
      continue;
    }
    have.add(column.field);
    // A linked column travels as its link and view, never as the copy of the
    // source's format that a materialised one would leave behind.
    added.push(isLinkedColumn(column) ? columnStub(column) : structuredClone(column));
  }
  return { columns: [...current, ...added], skipped };
}

/**
 * The library with the picked conditions appended, and the names skipped.
 *
 * A condition is the same one when it has the same **id** — copied before — or
 * the same **name**, which is what the picker shows: two rows reading *Impulse
 * up* cannot be told apart. The id is kept, so a filter that names it keeps
 * working on the screen it was copied to.
 */
export function copyConditions(
  current: readonly SavedScreenerFilter[],
  picked: readonly SavedScreenerFilter[],
): { savedFilters: SavedScreenerFilter[]; skipped: string[] } {
  const ids = new Set(current.map((f) => f.id));
  const names = new Set(current.map((f) => f.name.trim()));
  const skipped: string[] = [];
  const added: SavedScreenerFilter[] = [];
  for (const filter of picked) {
    // **A sealed condition is skipped, never copied.** It is the source screen's
    // rule named by id, evaluated by the server from *that* screen — copied
    // here it would name a rule this screen does not have, and it could only be
    // made to work by holding the rule, which is what sealing forbids. Copied
    // still sealed, it would tick and then hold every row back forever.
    // Linking (Part 2 of `docs/plans/screen-links.md`) is how one travels.
    // A **linked** one is not: it carries no rule, only where the rule is, so
    // it travels as the link it is (owner, 2026-10-07) and stays in step with
    // its source on the screen it lands on too.
    if (
      (isSealed(filter) && !filter.link) ||
      ids.has(filter.id) ||
      names.has(filter.name.trim())
    ) {
      skipped.push(filter.name);
      continue;
    }
    ids.add(filter.id);
    names.add(filter.name.trim());
    added.push(isLinkedCondition(filter) ? conditionStub(filter) : structuredClone(filter));
  }
  return { savedFilters: [...current, ...added], skipped };
}
